import { useState, useEffect } from 'react';
import { useHolidays } from './useHolidays';
import { hexToRgba, fmtDate, DAY_CELL_HEIGHT, DAY_TIME_SLOTS, DAY_SLOT_MINUTES, WEEKDAYS_ES, WEEKDAYS_EN, MONTHS_ES, MONTHS_EN, timeToMinutesFrom8 } from './agendaConstants';
import { ApptChip } from './ApptChip';
import { ScheduleSlotCell } from './ScheduleSlotHover';

export function DayView({ date, appointments, studies, onClickAppt, onClickSlot, onClickProgrammedSlot, lang, onDragStart, onDrop, scheduleSlots, slotLocks = [], scheduleBlocks = [], onContextMenuSlot, onClickBlock }) {
  const dayStr = fmtDate(date);
  const holidayName = useHolidays()[dayStr];
  const dayAppts = appointments.filter(a => a.date === dayStr);
  const studyMap = {};
  studies.forEach(s => { studyMap[s.study_id] = s; });
  const months = lang === 'es' ? MONTHS_ES : MONTHS_EN;
  const weekdays = lang === 'es' ? WEEKDAYS_ES : WEEKDAYS_EN;
  const dayOfWeek = date.getDay();
  const wdIdx = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const [dragOver, setDragOver] = useState(null);
  // URS-F196: indice rapido de locks por slot
  const lockByKey = {};
  slotLocks.forEach(lk => {
    lockByKey[`${lk.date}|${lk.study_id}|${lk.visit_code}|${lk.start_time}`] = lk;
  });

  // "Now" red line - only when this DayView shows today's date.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = () => setNow(new Date());
    const id = setInterval(tick, 60 * 1000); // refresh every minute
    return () => clearInterval(id);
  }, []);
  const today = new Date();
  const isToday = today.getFullYear() === date.getFullYear()
    && today.getMonth() === date.getMonth()
    && today.getDate() === date.getDate();
  const nowOffsetMin = now.getHours() * 60 + now.getMinutes() - 8 * 60;
  const totalMin = DAY_TIME_SLOTS.length * DAY_SLOT_MINUTES;
  const showNow = isToday && nowOffsetMin >= 0 && nowOffsetMin <= totalMin;
  const nowTopPx = (nowOffsetMin / DAY_SLOT_MINUTES) * DAY_CELL_HEIGHT;
  const nowLabel = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  // Schedule slots check uses a +/- 5-min tolerance so a slot configured at
  // 09:30-11:00 still highlights every 5-min row in that range.
  const getSlotsForCell = (slotLabel) => {
    return (scheduleSlots || []).filter(s => s.date === dayStr && s.start_time <= slotLabel && s.end_time > slotLabel);
  };

  // URS-F363: lane packing por intervalos (sustituye al agrupamiento por
  // hora exacta de inicio, que provocaba que citas que se solapan parcialmente
  // — p.ej. 08:25-08:55 y 08:30-09:00 — se renderizaran encima unas de otras).
  // 1) Ordenar citas por minuto de inicio (asc) y duracion (desc) para que las
  //    mas largas vayan a las primeras lanes.
  // 2) Construir clusters de citas que se solapan transitivamente.
  // 3) Dentro de cada cluster, asignar a cada cita la lane mas baja sin solape.
  // 4) widthPct = 100 / lanesEnElCluster, leftPct = lane * widthPct.
  const positionByAppt = {};
  {
    const items = dayAppts.map(appt => {
      const startMin = timeToMinutesFrom8(appt.time || '08:00');
      const dur = appt.duration_minutes || 15;
      return { appt, startMin, endMin: startMin + dur };
    }).sort((a, b) => (a.startMin - b.startMin) || (b.endMin - a.endMin));

    let i = 0;
    while (i < items.length) {
      // Construir un cluster: avanzar mientras la nueva cita empieza antes de
      // que termine la cita mas tardia ya incluida.
      let clusterEnd = items[i].endMin;
      let j = i + 1;
      while (j < items.length && items[j].startMin < clusterEnd) {
        clusterEnd = Math.max(clusterEnd, items[j].endMin);
        j++;
      }
      const cluster = items.slice(i, j);
      // Lane assignment dentro del cluster
      const lanes = []; // lanes[k] = endMin de la ultima cita en esa lane
      cluster.forEach(it => {
        let placed = -1;
        for (let k = 0; k < lanes.length; k++) {
          if (lanes[k] <= it.startMin) { placed = k; break; }
        }
        if (placed === -1) { placed = lanes.length; lanes.push(0); }
        lanes[placed] = it.endMin;
        it.lane = placed;
      });
      const totalLanes = Math.max(1, lanes.length);
      cluster.forEach(it => {
        positionByAppt[it.appt.appointment_id] = {
          leftPct: (it.lane * 100) / totalLanes,
          widthPct: 100 / totalLanes,
        };
      });
      i = j;
    }
  }

  const getApptPosition = (appt) => {
    return positionByAppt[appt.appointment_id] || { leftPct: 0, widthPct: 100 };
  };

  return (
    <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
      <div className={`px-4 py-3 border-b border-stone-200 ${holidayName ? 'bg-amber-50' : 'bg-stone-50'}`}>
        <p className="text-sm font-semibold text-stone-800">
          {weekdays[wdIdx]} {date.getDate()} {months[date.getMonth()]} {date.getFullYear()}
          {holidayName && <span className="ml-2 text-xs font-medium text-amber-700" data-testid="day-holiday-label">· {lang === 'es' ? 'Festivo' : 'Holiday'}: {holidayName}</span>}
        </p>
      </div>
      <div className={`max-h-[700px] overflow-y-auto ${holidayName ? 'bg-[repeating-linear-gradient(135deg,rgba(217,119,6,0.07)_0px,rgba(217,119,6,0.07)_6px,transparent_6px,transparent_12px)]' : ''}`}>
        <div className="flex">
          <div className="w-16 shrink-0 border-r border-stone-100">
            {DAY_TIME_SLOTS.map(slot => {
              const isHour = slot.m === 0;
              const isQuarter = slot.m === 15 || slot.m === 30 || slot.m === 45;
              const borderClass = isHour
                ? 'border-b border-stone-200'
                : (isQuarter ? 'border-b border-stone-100' : 'border-b border-stone-50');
              return (
                <div key={slot.label} style={{ height: DAY_CELL_HEIGHT, lineHeight: `${DAY_CELL_HEIGHT}px` }}
                  className={`px-2 flex items-center justify-end ${borderClass} ${isHour ? 'text-[11px] font-mono font-semibold text-stone-600' : (isQuarter ? 'text-[9px] font-mono text-stone-400' : 'text-[8px] font-mono text-stone-300')}`}>
                  <span style={{ lineHeight: 1 }}>{slot.label}</span>
                </div>
              );
            })}
          </div>
          <div className="flex-1 relative">
            {DAY_TIME_SLOTS.map(slot => {
              const isHour = slot.m === 0;
              const isQuarter = slot.m === 15 || slot.m === 30 || slot.m === 45;
              const isDragTarget = dragOver === slot.label;
              const cellSchedule = getSlotsForCell(slot.label);
              const borderClass = isHour
                ? 'border-b border-stone-200'
                : (isQuarter ? 'border-b border-stone-100' : 'border-b border-stone-50');
              return (
                <div key={slot.label} style={{ height: DAY_CELL_HEIGHT }}
                  className={`cursor-pointer relative ${borderClass} ${isDragTarget ? 'bg-[#1f3e80]/10' : 'hover:bg-stone-50/50'} transition-colors`}
                  onClick={() => onClickSlot(dayStr, slot.label)}
                  onContextMenu={(e) => {
                    if (!onContextMenuSlot) return;
                    e.preventDefault();
                    onContextMenuSlot(dayStr, slot.label, e.clientX, e.clientY);
                  }}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(slot.label); }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(null); onDrop(dayStr, slot.label); }}>
                  {cellSchedule.length > 0 && (
                    <div className="absolute inset-0 flex">
                      {cellSchedule.map((cs, ci) => {
                        const study = studyMap[cs.study_id];
                        const color = study?.color || '#888';
                        // URS-F196: detecta lock por (date, study, visit, start_time del slot)
                        const lockKey = `${dayStr}|${cs.study_id}|${cs.visit_code}|${cs.start_time}`;
                        const lock = lockByKey[lockKey];
                        const clickable = !!onClickProgrammedSlot;
                        const handleProgrammedClick = (e) => {
                          e.stopPropagation();
                          if (lock) return; // bloqueado por otro usuario
                          if (clickable) {
                            onClickProgrammedSlot({
                              study_id: cs.study_id,
                              visit_code: cs.visit_code,
                              date: dayStr,
                              start_time: cs.start_time,
                              end_time: cs.end_time,
                            });
                          } else {
                            onClickSlot(dayStr, slot.label);
                          }
                        };
                        return (
                          <ScheduleSlotCell
                            key={ci}
                            slot={cs}
                            study={study}
                            lock={lock}
                            isHour={isHour}
                            onClick={handleProgrammedClick}
                            dayStr={dayStr}
                            appointments={appointments}
                            lang={lang}
                            borderWidth={3}
                            testId={`schedule-cell-${cs.study_id}-${cs.visit_code}-${slot.label}`}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {(() => {
              // URS-F252: bloqueos del dia (globales + por estudio) renderizados
              // como bandas con patron diagonal rojo.
              const dayBlocks = (scheduleBlocks || []).filter(b => b.date === dayStr);
              if (!dayBlocks.length) return null;
              return (
                <div className="absolute inset-0" style={{ pointerEvents: 'none', zIndex: 8 }}>
                  {dayBlocks.map(b => {
                    const startMin = timeToMinutesFrom8(b.start_time);
                    const endMin = timeToMinutesFrom8(b.end_time);
                    const top = (startMin / DAY_SLOT_MINUTES) * DAY_CELL_HEIGHT;
                    const height = Math.max(0, ((endMin - startMin) / DAY_SLOT_MINUTES) * DAY_CELL_HEIGHT);
                    if (height <= 0) return null;
                    const isGlobal = !b.study_id;
                    const study = !isGlobal ? studyMap[b.study_id] : null;
                    const bg = isGlobal
                      ? 'repeating-linear-gradient(45deg, rgba(220,38,38,0.28) 0px, rgba(220,38,38,0.28) 8px, rgba(220,38,38,0.12) 8px, rgba(220,38,38,0.12) 16px)'
                      : 'repeating-linear-gradient(45deg, rgba(220,38,38,0.18) 0px, rgba(220,38,38,0.18) 6px, rgba(220,38,38,0.06) 6px, rgba(220,38,38,0.06) 12px)';
                    return (
                      <div
                        key={b.block_id}
                        data-testid={`schedule-block-${b.block_id}`}
                        onClick={(e) => { e.stopPropagation(); if (onClickBlock) onClickBlock(b); }}
                        title={`${isGlobal ? (lang === 'es' ? 'Bloqueo global' : 'Global block') : (study?.code || b.study_id)} · ${b.start_time}-${b.end_time} · ${b.reason || ''}`}
                        style={{
                          position: 'absolute', top, height, left: 0, right: 0,
                          background: bg,
                          borderLeft: isGlobal ? '3px solid #dc2626' : '3px solid rgba(220,38,38,0.5)',
                          borderTop: '1px solid rgba(220,38,38,0.4)',
                          borderBottom: '1px solid rgba(220,38,38,0.4)',
                          pointerEvents: 'auto',
                          cursor: onClickBlock ? 'pointer' : 'default',
                        }}
                      >
                        <div className="px-2 py-0.5 text-[10px] font-medium text-red-800 truncate select-none">
                          {isGlobal ? '🔒 ' : ''}{isGlobal ? (lang === 'es' ? 'Bloqueo global' : 'Global block') : (study?.code || b.study_id)}
                          {b.reason ? <span className="text-red-700/80 ml-1">· {b.reason}</span> : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            <div className="absolute inset-0" style={{ pointerEvents: 'none', zIndex: 10 }}>
              {dayAppts.map(appt => {
                const offsetMin = timeToMinutesFrom8(appt.time);
                const duration = appt.duration_minutes || 15;
                const spanCells = Math.max(1, Math.round(duration / DAY_SLOT_MINUTES));
                const top = (offsetMin / DAY_SLOT_MINUTES) * DAY_CELL_HEIGHT;
                const height = spanCells * DAY_CELL_HEIGHT;
                const { leftPct, widthPct } = getApptPosition(appt);
                const study = studyMap[appt.study_id];
                return (
                  <div key={appt.appointment_id} style={{ position: 'absolute', top, height, left: `${leftPct}%`, width: `${widthPct}%`, pointerEvents: 'auto', padding: '0 2px' }}>
                    <ApptChip appt={appt} study={study} onClick={onClickAppt} onDragStart={onDragStart} spanCells={spanCells} />
                  </div>
                );
              })}
            </div>
            {showNow && (
              <div
                data-testid="agenda-now-line"
                className="absolute left-0 right-0 pointer-events-none"
                style={{ top: nowTopPx - 1, zIndex: 20 }}
              >
                <div className="relative h-0.5 bg-red-500/90">
                  <span
                    className="absolute -left-12 -top-2.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-red-500 text-white text-[10px] font-mono font-semibold leading-none shadow-sm"
                    style={{ width: 'auto' }}
                  >
                    {nowLabel}
                  </span>
                  <span className="absolute -left-1.5 -top-1.5 w-3 h-3 rounded-full bg-red-500 ring-2 ring-white" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
